import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import esTranslations from './locales/es';
import enTranslations from './locales/en';

// Constants for language persistence
const LANGUAGE_STORAGE_KEY = 'cotizatrin_user_language';

// Get saved language from localStorage or default to Spanish
const getSavedLanguage = (): string => {
  try {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage && ['es', 'en', 'fr', 'zh'].includes(savedLanguage)) {
      console.log('Saved language found in localStorage:', savedLanguage);
      return savedLanguage;
    }
  } catch (error) {
    console.warn('Error reading saved language from localStorage:', error);
  }
  
  console.log('No saved language found, defaulting to Spanish');
  return 'es'; // Default to Spanish
};

// Export resources for external use
export const resources = {
  es: {
    translation: esTranslations
  },
  en: {
    translation: enTranslations
  },
  fr: {
    translation: enTranslations // Use English as fallback for French
  },
  zh: {
    translation: enTranslations // Use English as fallback for Chinese
  }
};

// Get the initial language
const initialLanguage = getSavedLanguage();

// Initialize i18n immediately when this module is imported
i18n
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es', // Fallback to Spanish if a translation is missing
    lng: initialLanguage, // Use saved language or default to Spanish
    debug: false, // Disable debug to prevent console output
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false
    },
    returnEmptyString: false,
    saveMissing: false, // Disable saving missing keys
    react: {
      useSuspense: false // Disable suspense to prevent loading issues
    }
  });

// Ensure i18n is ready
if (!i18n.isInitialized) {
  console.log('i18n is initializing...');
}

// Helper for debugging
if (import.meta.env.DEV) {
  window.i18n = i18n;
}

// Export helper functions for language persistence
export const saveLanguageToStorage = (language: string): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    console.log('Language saved to localStorage:', language);
  } catch (error) {
    console.warn('Error saving language to localStorage:', error);
  }
};

export const hasSavedLanguage = (): boolean => {
  try {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return savedLanguage !== null && ['es', 'en', 'fr', 'zh'].includes(savedLanguage);
  } catch (error) {
    console.warn('Error checking for saved language:', error);
    return false;
  }
};

// Export the initialized i18n instance as default
export default i18n;